import React, { ComponentType } from 'react'

import { useDashboardData } from 'hooks/DashboardDataContext'

import ConnectionStatusCardComponent from 'pages/Dashboard/cards/ConnectionStatusCard'
import CriticalAndMajorAlertsMapCard from 'pages/Dashboard/cards/CriticalAndMajorAlertsMapCard'
import CustomerActivityCard from 'pages/Dashboard/cards/CustomerActivityCard'
import CustomerAdoptionCardComponent from 'pages/Dashboard/cards/CustomerAdoptionCard'
import DisconnectedDevicesCardComponent from 'pages/Dashboard/cards/DisconnectedDevicesCard'
import EquipmentAlertsCardComponent from 'pages/Dashboard/cards/EquipmentAlertsCard'
import HehmDeviceStatusAlertsComponent from 'pages/Dashboard/cards/HehmDeviceStatusAlerts'
import HehmHealthCardComponent from 'pages/Dashboard/cards/HehmHealthCard'
import PinnedCustomersCard from 'pages/Dashboard/cards/PinnedCustomersCard'
import SdaAlertsCardComponent from 'pages/Dashboard/cards/SdaAlertsCard'
import ServiceContractsCardComponent from 'pages/Dashboard/cards/ServiceContractsCard'

export type CardId =
  | 'connection_status'
  | 'critical_and_major_alerts_map'
  | 'recent_activity'
  | 'customer_adoption'
  | 'disconnected_devices'
  | 'equipment_alerts'
  | 'thermostat_alerts'
  | 'pmk_health'
  | 'pinned_customers'
  | 'sda_alerts'
  | 'service_contracts'

export type DashboardCard = ComponentType & { cardTitle: string; cardKey: string }

export type CardConfig = {
  colSpan: 1 | 2
  Component: DashboardCard
}

const withCardDetails = (fn: () => JSX.Element, source: DashboardCard): DashboardCard => {
  return Object.assign(fn, { cardTitle: source.cardTitle, cardKey: source.cardKey })
}

const EquipmentAlertsCard = withCardDetails(() => {
  const { dealer, isDealerLoaded } = useDashboardData()

  return (
    <EquipmentAlertsCardComponent
      criticalAlertCount={dealer.criticalAlertCount}
      majorAlertCount={dealer.majorAlertCount}
      minorAlertCount={dealer.minorAlertCount}
      isLoaded={isDealerLoaded}
    />
  )
}, EquipmentAlertsCardComponent)

const SdaAlertsCard = withCardDetails(() => {
  const { dealer, isDealerLoaded } = useDashboardData()

  return (
    <SdaAlertsCardComponent
      criticalSdaCount={dealer.sdaCriticalAlertCount + dealer.hehmSDACriticalAlarmCount}
      sdaMajorAlertCount={dealer.sdaMajorAlertCount + dealer.hehmSDAMajorAlarmCount}
      sdaMinorAlertCount={dealer.sdaMinorAlertCount + dealer.hehmSDAMinorAlarmCount}
      dirtyFilterCount={dealer.dirtyFilterCount}
      isLoaded={isDealerLoaded}
    />
  )
}, SdaAlertsCardComponent)

const ConnectionStatusCard = withCardDetails(() => {
  const { dealer, isDealerLoaded } = useDashboardData()

  return (
    <ConnectionStatusCardComponent
      connectedCount={dealer.connectedCount}
      disconnectedCount={dealer.disconnectedCount}
      isLoaded={isDealerLoaded}
    />
  )
}, ConnectionStatusCardComponent)

const ServiceContractsCard = withCardDetails(() => {
  const { dealer, isDealerLoaded, enabledFeatures } = useDashboardData()

  return (
    <ServiceContractsCardComponent
      activeServiceContractCount={dealer.activeServiceContractCount}
      expiredServiceContractCount={dealer.expiredServiceContractCount}
      nonServiceContractCount={dealer.nonServiceContractCount}
      isLoaded={isDealerLoaded}
      hehmEnabled={enabledFeatures.includes('hehm')}
    />
  )
}, ServiceContractsCardComponent)

const CustomerAdoptionCard = withCardDetails(() => {
  const { enabledFeatures } = useDashboardData()

  return <CustomerAdoptionCardComponent hehmEnabled={enabledFeatures.includes('hehm')} />
}, CustomerAdoptionCardComponent)

const HehmHealthCard = withCardDetails(() => {
  const { dealer, isDealerLoaded } = useDashboardData()

  return (
    <HehmHealthCardComponent
      healthScores={{
        critical: dealer.hehmHealthScoreCriticalCount,
        fair: dealer.hehmHealthScoreFairCount,
        great: dealer.hehmHealthScoreGreatCount
      }}
      filterHealthScores={{
        fair: dealer.hehmFilterHealthScoreFairCount,
        needsReplacement: dealer.hehmFilterHealthScoreNeedsReplacementCount
      }}
      isLoaded={isDealerLoaded}
    />
  )
}, HehmHealthCardComponent)

const DisconnectedDevicesCard = withCardDetails(() => {
  const { dealer } = useDashboardData()

  return (
    <DisconnectedDevicesCardComponent
      recentlyDisconnectedCount={dealer.hehmRecentlyDisconnectedCount}
      disconnectedIn24HoursCount={dealer.hehmDisconnected24HoursCount}
      disconnectedIn7DaysCount={dealer.hehmDisconnected7DaysCount}
    />
  )
}, DisconnectedDevicesCardComponent)

const HehmDeviceStatusAlerts = withCardDetails(() => {
  const { dealer, isDealerLoaded } = useDashboardData()

  return (
    <HehmDeviceStatusAlertsComponent
      criticalAlertCount={dealer.criticalAlertCount}
      majorAlertCount={dealer.majorAlertCount}
      minorAlertCount={dealer.minorAlertCount}
      criticalSdaAlertCount={dealer.sdaCriticalAlertCount + dealer.hehmSDACriticalAlarmCount}
      sdaMajorAlertCount={dealer.sdaMajorAlertCount + dealer.hehmSDAMajorAlarmCount}
      sdaMinorAlertCount={dealer.sdaMinorAlertCount + dealer.hehmSDAMinorAlarmCount}
      dirtyFilterCount={dealer.dirtyFilterCount}
      isLoaded={isDealerLoaded}
    />
  )
}, HehmDeviceStatusAlertsComponent)

const card = (config: CardConfig): CardConfig => config

export const HEHM_CARD_REGISTRY: Record<string, CardConfig> = {
  [CustomerActivityCard.cardKey]: card({ colSpan: 2, Component: CustomerActivityCard }),
  [HehmHealthCard.cardKey]: card({ colSpan: 1, Component: HehmHealthCard }),
  [HehmDeviceStatusAlerts.cardKey]: card({ colSpan: 1, Component: HehmDeviceStatusAlerts }),
  [DisconnectedDevicesCard.cardKey]: card({ colSpan: 1, Component: DisconnectedDevicesCard }),
  [ServiceContractsCard.cardKey]: card({ colSpan: 1, Component: ServiceContractsCard }),
  [CustomerAdoptionCard.cardKey]: card({ colSpan: 1, Component: CustomerAdoptionCard })
}

export const NON_HEHM_CARD_REGISTRY: Record<string, CardConfig> = {
  [EquipmentAlertsCard.cardKey]: card({ colSpan: 1, Component: EquipmentAlertsCard }),
  [SdaAlertsCard.cardKey]: card({ colSpan: 1, Component: SdaAlertsCard }),
  [ConnectionStatusCard.cardKey]: card({ colSpan: 1, Component: ConnectionStatusCard }),
  [ServiceContractsCard.cardKey]: card({ colSpan: 1, Component: ServiceContractsCard }),
  [PinnedCustomersCard.cardKey]: card({ colSpan: 1, Component: PinnedCustomersCard }),
  [CriticalAndMajorAlertsMapCard.cardKey]: card({ colSpan: 2, Component: CriticalAndMajorAlertsMapCard }),
  [CustomerAdoptionCard.cardKey]: card({ colSpan: 1, Component: CustomerAdoptionCard })
}
